/**
 * Landing page: hero promise, trust band, the three product paths,
 * editor capabilities, pricing close, and SEO guides.
 * Kept short on purpose — one job per section for retention.
 */

import React, { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import Seo, { SITE_URL } from '../components/Seo';
import LandingHeroBeforeAfter from '../components/LandingHeroBeforeAfter';
import LandingTemplates from '../components/LandingTemplates';
import { APP_HOME, REWRITE, REGISTER_TO } from '../utils/paths';
import { getLocalizedPost, getPostBySlug } from '../content/blog/posts';
import './Landing.css';

const LANDING_GUIDE_SLUGS = ['cv-canva-y-ats', 'revisar-cv-online-ats', 'cv-para-portales-empleo'];

const HERO_STEP_KEYS = ['landing.heroStep1', 'landing.heroStep2', 'landing.heroStep3'];

const BLANK_CV_PATH = '/dashboard/edit-cv';
const ATS_PATH = '/dashboard/upload';

const OG_IMAGE = `${SITE_URL}/landing/og-share.png`;

const EDITOR_FEATURES = [
  { id: 'sections', titleKey: 'landing.editor1Title', textKey: 'landing.editor1Text' },
  { id: 'language', titleKey: 'landing.editor2Title', textKey: 'landing.editor2Text' },
  { id: 'templates', titleKey: 'landing.editor3Title', textKey: 'landing.editor3Text' },
  { id: 'preview', titleKey: 'landing.editor4Title', textKey: 'landing.editor4Text' },
  { id: 'download', titleKey: 'landing.editor5Title', textKey: 'landing.editor5Text' },
];

const PATH_CARDS = [
  {
    id: 'rewrite',
    to: REWRITE,
    eyebrowKey: 'landing.pathsFileEyebrow',
    titleKey: 'landing.pathRewriteTitle',
    textKey: 'landing.pathRewriteText',
    ctaKey: 'landing.pathRewriteCta',
    featured: true,
  },
  {
    id: 'ats',
    to: ATS_PATH,
    eyebrowKey: 'landing.pathsFileEyebrow',
    titleKey: 'landing.pathAtsTitle',
    textKey: 'landing.pathAtsText',
    ctaKey: 'landing.pathAtsCta',
    featured: false,
  },
  {
    id: 'blank',
    to: BLANK_CV_PATH,
    eyebrowKey: 'landing.pathsBlankEyebrow',
    titleKey: 'landing.pathBlankTitle',
    textKey: 'landing.pathBlankText',
    ctaKey: 'landing.pathBlankCta',
    featured: false,
  },
];

const Landing = () => {
  const { isAuthenticated } = useAuth();
  const { t, lang, localePath } = useLanguage();

  const jsonLd = useMemo(
    () => ({
      '@context': 'https://schema.org',
      '@type': 'SoftwareApplication',
      name: 'ClarCV',
      applicationCategory: 'BusinessApplication',
      operatingSystem: 'Web',
      description: t('landing.metaDescription'),
      url: `${SITE_URL}${localePath('/')}`,
      image: OG_IMAGE,
      inLanguage: [lang === 'en' ? 'en' : 'es', lang === 'en' ? 'es' : 'en'],
      offers: {
        '@type': 'AggregateOffer',
        lowPrice: '0',
        highPrice: '7.00',
        priceCurrency: 'EUR',
        offerCount: 2,
        offers: [
          {
            '@type': 'Offer',
            name: 'ClarCV Free',
            price: '0',
            priceCurrency: 'EUR',
            description: t('landing.trustPriceText'),
          },
          {
            '@type': 'Offer',
            name: 'ClarCV Pro',
            price: '7.00',
            priceCurrency: 'EUR',
            description: t('landing.trustPayText'),
          },
        ],
      },
    }),
    [t, lang, localePath]
  );

  const primaryCta = isAuthenticated ? (
    <Link to={APP_HOME} className="btn btn-primary">
      {t('landing.ctaDashboard')}
    </Link>
  ) : (
    <Link to={REWRITE} className="btn btn-primary">
      {t('landing.ctaStart')}
    </Link>
  );

  return (
    <div className="landing">
      <Seo
        title={t('landing.metaTitle')}
        description={t('landing.metaDescription')}
        path="/"
        image={OG_IMAGE}
        lang={lang}
        hreflang
        jsonLd={jsonLd}
      />

      <section className="landing-hero" aria-label={t('landing.ariaHero')}>
        <div className="landing-hero__bg" aria-hidden="true" />
        <div className="container landing-hero__grid">
          <p className="landing-hero__eyebrow">{t('landing.heroEyebrow')}</p>
          <h1 className="landing-hero__title">
            {t('landing.heroTitleLead')}{' '}
            <span className="landing-hero__title-accent">{t('landing.heroTitleAccent')}</span>
          </h1>
          <ol className="landing-hero__steps">
            {HERO_STEP_KEYS.map((key) => (
              <li key={key}>{t(key)}</li>
            ))}
          </ol>
          <div className="landing-hero__cta">
            {primaryCta}
            <Link to={BLANK_CV_PATH} className="btn btn-outline landing-hero__cta-alt">
              {t('landing.ctaBlank')}
            </Link>
          </div>
          <p className="landing-hero__fine">{t('landing.heroFine')}</p>
          <p className="landing-hero__ai">
            <span className="landing-hero__ai-label">{t('landing.heroAiLabel')}</span>
            <span className="landing-hero__ai-text">{t('landing.heroAiText')}</span>
          </p>
          <div className="landing-hero__visual" aria-hidden="true">
            <span className="landing-hero__glow" />
            <LandingHeroBeforeAfter lang={lang} />
          </div>
        </div>
      </section>

      <section className="landing-trust" aria-label={t('landing.ariaTrust')}>
        <div className="container landing-trust__row">
          <p>
            <strong>{t('landing.trustFiles')}</strong>
            <span>{t('landing.trustFilesText')}</span>
          </p>
          <p>
            <strong>{t('landing.trustPrivacy')}</strong>
            <span>
              <Link to={localePath('/blog/privacidad-ia-cv')}>{t('landing.trustPrivacyShort')}</Link>
            </span>
          </p>
          <p>
            <strong>{t('landing.trustLang')}</strong>
            <span>{t('landing.trustLangText')}</span>
          </p>
        </div>
      </section>

      <section className="landing-paths" id="como-funciona" aria-labelledby="landing-paths-title">
        <div className="container">
          <h2 className="landing-paths__title" id="landing-paths-title">
            {t('landing.pathsTitle')}
          </h2>
          <p className="landing-paths__lead">{t('landing.pathsLead')}</p>

          <ul className="landing-paths__grid">
            {PATH_CARDS.map((card) => (
              <li key={card.id}>
                <Link
                  to={card.to}
                  className={
                    card.featured
                      ? 'landing-path-card landing-path-card--featured'
                      : 'landing-path-card'
                  }
                >
                  <p className="landing-path-card__eyebrow">{t(card.eyebrowKey)}</p>
                  <h3 className="landing-path-card__title">{t(card.titleKey)}</h3>
                  <p className="landing-path-card__text">{t(card.textKey)}</p>
                  <span className="landing-path-card__cta">{t(card.ctaKey)}</span>
                </Link>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <LandingTemplates />

      <section className="landing-editor" aria-labelledby="landing-editor-title">
        <div className="container">
          <h2 className="landing-editor__title" id="landing-editor-title">
            {t('landing.editorTitle')}
          </h2>
          <p className="landing-editor__lead">{t('landing.editorLead')}</p>
          <ul className="landing-editor__grid">
            {EDITOR_FEATURES.map((feature) => (
              <li
                key={feature.id}
                className={`landing-editor__card landing-editor__card--${feature.id}`}
              >
                <span className="landing-editor__icon" aria-hidden="true" />
                <h3 className="landing-editor__card-title">{t(feature.titleKey)}</h3>
                <p className="landing-editor__card-text">{t(feature.textKey)}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="landing-price" id="precios-resumen" aria-labelledby="landing-price-title">
        <div className="container landing-price__inner">
          <div>
            <h2 className="landing-price__title" id="landing-price-title">
              {t('landing.priceTitle')}
            </h2>
            <p className="landing-price__lead">{t('landing.priceLead')}</p>
          </div>
          <div className="landing-price__actions">
            <p className="landing-price__amount">
              {t('landing.priceAmount')}
              <span className="landing-price__amount-label">
                {t('landing.priceAmountLabel')}
              </span>
            </p>
            {isAuthenticated ? (
              <Link to={APP_HOME} className="btn btn-primary">
                {t('landing.ctaDashboard')}
              </Link>
            ) : (
              <Link to={REGISTER_TO} className="btn btn-primary">
                {t('landing.priceCtaStart')}
              </Link>
            )}
            <div className="landing-price__secondary">
              <p className="landing-price__pro-note">{t('landing.priceProNote')}</p>
              <Link to={localePath('/pricing')} className="landing-price__link">
                {t('landing.priceLink')}
              </Link>
            </div>
          </div>
        </div>
      </section>

      <section className="landing-blog" aria-labelledby="landing-blog-title">
        <div className="container landing-blog__inner">
          <div>
            <h2 className="landing-blog__title" id="landing-blog-title">
              {t('landing.blogTitle')}
            </h2>
            <p className="landing-blog__lead">{t('landing.blogLead')}</p>
            <ul className="landing-blog__featured">
              {LANDING_GUIDE_SLUGS.map((slug) => {
                const post = getPostBySlug(slug);
                if (!post) return null;
                const loc = getLocalizedPost(post, lang);
                return (
                  <li key={slug}>
                    <Link to={localePath(`/blog/${slug}`)}>{loc.title}</Link>
                  </li>
                );
              })}
            </ul>
          </div>
          <Link to={localePath('/blog')} className="btn btn-ghost">
            {t('landing.blogCta')}
          </Link>
        </div>
      </section>
    </div>
  );
};

export default Landing;
